import React, { useCallback, useEffect, useRef, useState } from "react";
import { observer } from "mobx-react-lite";
import { container } from "@/utils/mobx";
import { appStore } from "@/app_store";
import { BrowserStreamModalStore, NETWORK_FILTERS, matchesNetworkFilter } from "./store";
import type { NetworkFilter } from "./store";
import { keyToKeysym } from "@/utils/keysym";
import { SWIPE_GUARD_CLASS } from "@/utils/swipe";
import { TextInput } from "@/components/toolkit/TextInput";
import "../ProxyModal/s.proxy_modal.css";

const DESKTOP_WIDTH = 1280;
const DESKTOP_HEIGHT = 720;

const DESKTOP_PRESETS = [
    { label: "Full HD", desc: "1920×1080", width: 1920, height: 1080 },
    { label: "HD+",     desc: "1440×900",  width: 1440, height: 900  },
    { label: "HD",      desc: "1280×720",  width: 1280, height: 720  },
    { label: "WXGA",    desc: "1366×768",  width: 1366, height: 768  },
    { label: "XGA",     desc: "1024×768",  width: 1024, height: 768  },
];

const MOBILE_PRESETS = [
    { label: "iPhone 14", desc: "390×844",  width: 390, height: 844  },
    { label: "iPhone SE", desc: "375×667",  width: 375, height: 667  },
    { label: "iPhone XR", desc: "414×896",  width: 414, height: 896  },
    { label: "Android M", desc: "360×800",  width: 360, height: 800  },
    { label: "iPad",      desc: "768×1024", width: 768, height: 1024 },
];

const QUALITY_PRESETS = [
    { label: "Ultra Low", desc: "200k · 10fps",  bitrate: 200,  framerate: 10 },
    { label: "Low",       desc: "500k · 15fps",  bitrate: 500,  framerate: 15 },
    { label: "Medium",    desc: "1.5M · 24fps",  bitrate: 1500, framerate: 24 },
    { label: "High",      desc: "2M · 30fps",    bitrate: 2000, framerate: 30 },
    { label: "Ultra",     desc: "4M · 30fps",    bitrate: 4000, framerate: 30 },
];


export const BrowserStreamModal = container((props: {
    store: BrowserStreamModalStore;
}) => {
    const store = props.store;
    const videoRef = useRef<HTMLVideoElement>(null);
    const scrollWrapperRef = useRef<HTMLDivElement>(null);
    const zoomWrapperRef = useRef<HTMLDivElement>(null);
    const overlayRef = useRef<HTMLDivElement>(null);
    const controlsRef = useRef<HTMLDivElement>(null);
    const [userZoom, setUserZoom] = useState(1);
    const [pinchEnabled, setPinchEnabled] = useState(true);
    const [urlInput, setUrlInput] = useState(store.url);
    const [displayConfigOpen, setDisplayConfigOpen] = useState(false);
    const [videoLoading, setVideoLoading] = useState(true);

    const urlInputFocusedRef = useRef(false);

    useEffect(() => { setUrlInput(store.url); }, [store.browserSessionId]);
    useEffect(() => {
        const url = store.session?.current_url;
        if (url && !urlInputFocusedRef.current) setUrlInput(url);
    }, [store.session?.current_url]);

    useEffect(() => {
        if (store.isOpen) setVideoLoading(true);
    }, [store.isOpen, store.browserSessionId, store.whepUrl]);

    useEffect(() => {
        const video = videoRef.current;
        if (!video) return;
        const onPlaying = () => setVideoLoading(false);
        video.addEventListener("playing", onPlaying);
        return () => video.removeEventListener("playing", onPlaying);
    }, []);
    const userZoomRef = useRef(1);
    const pinchEnabledRef = useRef(true);
    const pinchRef = useRef({ active: false, startDist: 0, startZoom: 1 });
    const lastSingleTouchRef = useRef<{ x: number; y: number } | null>(null);
    const tapStartRef = useRef<{ x: number; y: number } | null>(null);

    const applyDesktopScale = useCallback(() => {
        const video = videoRef.current;
        const wrapper = zoomWrapperRef.current;
        const scroller = scrollWrapperRef.current;
        if (!video || !scroller) return;
        const contentWidth = video.videoWidth || DESKTOP_WIDTH;
        const contentHeight = video.videoHeight || DESKTOP_HEIGHT;
        const w = scroller.clientWidth;
        const h = scroller.clientHeight;
        const baseScale = Math.min(1, w / contentWidth, h / contentHeight);
        const totalZoom = baseScale * userZoomRef.current;
        video.style.objectFit = "";
        video.style.width = `${contentWidth}px`;
        video.style.height = `${contentHeight}px`;
        video.style.zoom = String(totalZoom);
        video.style.transform = "";
        video.style.transformOrigin = "";
        if (wrapper) {
            wrapper.style.width = `${Math.round(contentWidth * totalZoom)}px`;
            wrapper.style.height = `${Math.round(contentHeight * totalZoom)}px`;
        }
    }, []);

    // Mobile: stretch the wrapper to fill the scroller, then let CSS object-fit contain
    // the stream inside it. This avoids zoom/scale math and handles any stream dimensions.
    const applyMobileScale = useCallback(() => {
        const video = videoRef.current;
        const wrapper = zoomWrapperRef.current;
        const scroller = scrollWrapperRef.current;
        if (!video || !scroller) return;
        video.style.zoom = "";
        video.style.transform = "";
        video.style.transformOrigin = "";
        video.style.width = "100%";
        video.style.height = "100%";
        video.style.objectFit = "contain";
        if (wrapper) {
            wrapper.style.width = `${scroller.clientWidth}px`;
            wrapper.style.height = `${scroller.clientHeight}px`;
        }
    }, []);

    useEffect(() => {
        const video = videoRef.current;
        const scroller = scrollWrapperRef.current;
        if (!video || !scroller || !store.isOpen) return;

        const applyScale = store.proxyViewMode === "desktop" ? applyDesktopScale : applyMobileScale;
        applyScale();
        const ro = new ResizeObserver(applyScale);
        ro.observe(scroller);
        return () => ro.disconnect();
    }, [store.proxyViewMode, store.isOpen, applyDesktopScale, applyMobileScale]);

    useEffect(() => {
        if (store.isOpen && store.proxyViewMode === "desktop") {
            applyDesktopScale();
        }
    }, [userZoom, store.isOpen, store.proxyViewMode, applyDesktopScale]);

    // Re-apply scale when video stream reconnects with new dimensions (after resize)
    useEffect(() => {
        const video = videoRef.current;
        if (!video) return;
        const onMeta = () => {
            if (store.proxyViewMode === "desktop") applyDesktopScale();
            else applyMobileScale();
        };
        video.addEventListener("loadedmetadata", onMeta);
        return () => video.removeEventListener("loadedmetadata", onMeta);
    }, [store.proxyViewMode, applyDesktopScale, applyMobileScale]);

    useEffect(() => {
        if (!store.isOpen || store.proxyViewMode !== "desktop") {
            userZoomRef.current = 1;
            setUserZoom(1);
            setPinchEnabled(true);
            pinchEnabledRef.current = true;
        }
    }, [store.isOpen, store.proxyViewMode]);

    // Close display config panel on click outside
    useEffect(() => {
        if (!displayConfigOpen) return;
        const handler = (e: MouseEvent) => {
            if (controlsRef.current && !controlsRef.current.contains(e.target as Node)) {
                setDisplayConfigOpen(false);
            }
        };
        document.addEventListener("mousedown", handler);
        return () => document.removeEventListener("mousedown", handler);
    }, [displayConfigOpen]);

    // WHEP video connection with retry backoff
    useEffect(() => {
        if (!store.isOpen || !store.whepUrl) return;
        const whepUrl = store.whepUrl;
        let cancelled = false;
        let activePc: RTCPeerConnection | null = null;

        const connect = async () => {
            let delay = 100;
            while (!cancelled) {
                const pc = new RTCPeerConnection();
                activePc = pc;
                pc.addTransceiver("video", { direction: "recvonly" });
                pc.ontrack = (e) => {
                    if (videoRef.current && e.streams[0]) videoRef.current.srcObject = e.streams[0];
                };
                try {
                    const offer = await pc.createOffer();
                    if (cancelled) { pc.close(); return; }
                    await pc.setLocalDescription(offer);
                    const res = await fetch(whepUrl, {
                        method: "POST",
                        headers: { "Content-Type": "application/sdp" },
                        body: pc.localDescription!.sdp,
                    });
                    if (cancelled) { pc.close(); return; }
                    if (!res.ok) {
                        pc.close();
                        activePc = null;
                        await new Promise(r => setTimeout(r, delay));
                        delay = Math.min(delay * 2, 1000);
                        continue;
                    }
                    const sdp = await res.text();
                    if (cancelled) { pc.close(); return; }
                    await pc.setRemoteDescription({ type: "answer", sdp });
                    delay = 100;
                    // Wait until the connection drops before retrying
                    await new Promise<void>(resolve => {
                        const onState = () => {
                            const s = pc.connectionState;
                            if (s === "failed" || s === "closed" || s === "disconnected") resolve();
                        };
                        pc.addEventListener("connectionstatechange", onState);
                        onState();
                    });
                    pc.close();
                    activePc = null;
                    if (cancelled) return;
                    setVideoLoading(true);
                    if (videoRef.current) videoRef.current.srcObject = null;
                    await new Promise(r => setTimeout(r, delay));
                    delay = Math.min(delay * 2, 1000);
                } catch (err) {
                    pc.close();
                    activePc = null;
                    if (cancelled) return;
                    await new Promise(r => setTimeout(r, delay));
                    delay = Math.min(delay * 2, 1000);
                }
            }
        };

        connect();

        return () => {
            cancelled = true;
            if (activePc) activePc.close();
            activePc = null;
            if (videoRef.current) videoRef.current.srcObject = null;
        };
    }, [store.isOpen, store.browserSessionId, store.whepUrl]);

    // TODO(app-model): every "browser_*" appStore.agent.request(...) call below (stream
    // keepalive, pointer/keyboard/console/resize/quality/navigate forwarding) no longer has
    // a backend handler -- old agent.api.handlers.browser was removed. BrowserSession already
    // exposes navigate/resize/set_quality/start_streaming/stop_streaming on the app model
    // (see BrowserSession.generated.ts); pointer/keyboard/console input has no equivalent
    // yet. Wire these once this component holds a BrowserSession entity instead of a bare
    // browser_session_id.

    // Touch and pinch-zoom handler (desktop mode)
    useEffect(() => {
        const el = overlayRef.current;
        if (!el || !store.isOpen || store.proxyViewMode !== "desktop") return;

        const sid = store.browserSessionId;

        const getTouchDist = (touches: TouchList) => {
            const dx = touches[0].clientX - touches[1].clientX;
            const dy = touches[0].clientY - touches[1].clientY;
            return Math.sqrt(dx * dx + dy * dy);
        };

        const getVideoPoint = (clientX: number, clientY: number) => {
            const video = videoRef.current;
            if (!video) return null;
            const rect = video.getBoundingClientRect();
            const zoom = parseFloat(video.style.zoom) || 1;
            return {
                x: Math.round((clientX - rect.left) / zoom),
                y: Math.round((clientY - rect.top) / zoom),
            };
        };

        const onStart = (e: TouchEvent) => {
            if (e.touches.length === 2 && pinchEnabledRef.current) {
                pinchRef.current = { active: true, startDist: getTouchDist(e.touches), startZoom: userZoomRef.current };
                lastSingleTouchRef.current = null;
                tapStartRef.current = null;
            } else if (e.touches.length === 1) {
                lastSingleTouchRef.current = { x: e.touches[0].clientX, y: e.touches[0].clientY };
                tapStartRef.current = { x: e.touches[0].clientX, y: e.touches[0].clientY };
                pinchRef.current.active = false;
            }
        };

        const onMove = (e: TouchEvent) => {
            if (pinchRef.current.active && e.touches.length === 2) {
                e.preventDefault();
                const dist = getTouchDist(e.touches);
                const newZoom = Math.max(1, Math.min(5, pinchRef.current.startZoom * dist / pinchRef.current.startDist));
                userZoomRef.current = newZoom;
                setUserZoom(newZoom);
            } else if (e.touches.length === 1 && lastSingleTouchRef.current) {
                e.preventDefault();
                const ddx = lastSingleTouchRef.current.x - e.touches[0].clientX;
                const ddy = lastSingleTouchRef.current.y - e.touches[0].clientY;
                if (pinchEnabledRef.current && userZoomRef.current > 1) {
                    const scroller = scrollWrapperRef.current;
                    if (scroller) {
                        scroller.scrollLeft += ddx;
                        scroller.scrollTop += ddy;
                    }
                } else {
                    void store.session?.scroll(ddy);
                }
                lastSingleTouchRef.current = { x: e.touches[0].clientX, y: e.touches[0].clientY };
            }
        };

        const onEnd = (e: TouchEvent) => {
            if (e.touches.length < 2) pinchRef.current.active = false;
            if (e.touches.length === 0) {
                const start = tapStartRef.current;
                const touch = e.changedTouches[0];
                if (start && touch) {
                    const dx = Math.abs(touch.clientX - start.x);
                    const dy = Math.abs(touch.clientY - start.y);
                    if (dx < 8 && dy < 8) {
                        e.preventDefault();
                        const pt = getVideoPoint(touch.clientX, touch.clientY);
                        const video = videoRef.current;
                        const vw = video?.videoWidth || DESKTOP_WIDTH;
                        const vh = video?.videoHeight || DESKTOP_HEIGHT;
                        if (pt && pt.x >= 0 && pt.y >= 0 && pt.x <= vw && pt.y <= vh) {
                            void store.session?.mouse_move(pt.x, pt.y);
                            void store.session?.mouse_button(0, true);
                            void store.session?.mouse_button(0, false);
                        }
                    }
                }
                lastSingleTouchRef.current = null;
                tapStartRef.current = null;
            }
        };

        el.addEventListener("touchstart", onStart, { passive: true });
        el.addEventListener("touchmove", onMove, { passive: false });
        el.addEventListener("touchend", onEnd, { passive: false });
        return () => {
            el.removeEventListener("touchstart", onStart);
            el.removeEventListener("touchmove", onMove);
            el.removeEventListener("touchend", onEnd);
        };
    }, [store.isOpen, store.proxyViewMode, store.browserSessionId]);

    // Mobile mode: touch tap/drag + mouse click/move/scroll (desktop browser emulation)
    useEffect(() => {
        const el = overlayRef.current;
        if (!el || !store.isOpen || store.proxyViewMode !== "mobile") return;
        const sid = store.browserSessionId;

        let lastTouch: { x: number; y: number } | null = null;
        let moved = false;

        // Map a client coordinate to stream coordinates, accounting for
        // object-fit: contain letterboxing/pillarboxing inside the video element.
        const getVideoPoint = (clientX: number, clientY: number) => {
            const video = videoRef.current;
            if (!video) return null;
            const streamW = video.videoWidth;
            const streamH = video.videoHeight;
            if (!streamW || !streamH) return null;
            const rect = video.getBoundingClientRect();
            const elemW = rect.width;
            const elemH = rect.height;
            if (!elemW || !elemH) return null;
            const streamAR = streamW / streamH;
            const elemAR = elemW / elemH;
            let vx: number, vy: number, vw: number, vh: number;
            if (elemAR > streamAR) {
                // pillarbox: bars on left and right
                vh = elemH;
                vw = elemH * streamAR;
                vx = rect.left + (elemW - vw) / 2;
                vy = rect.top;
            } else {
                // letterbox: bars on top and bottom
                vw = elemW;
                vh = elemW / streamAR;
                vx = rect.left;
                vy = rect.top + (elemH - vh) / 2;
            }
            return {
                x: Math.round((clientX - vx) / vw * streamW),
                y: Math.round((clientY - vy) / vh * streamH),
            };
        };

        // ── touch (real mobile device) ────────────────────────────────────────
        const onTouchStart = (e: TouchEvent) => {
            if (e.touches.length !== 1) return;
            lastTouch = { x: e.touches[0].clientX, y: e.touches[0].clientY };
            moved = false;
        };
        const onTouchMove = (e: TouchEvent) => {
            if (e.touches.length !== 1 || !lastTouch) return;
            const dx = lastTouch.x - e.touches[0].clientX;
            const dy = lastTouch.y - e.touches[0].clientY;
            if (!moved && (Math.abs(dx) > 4 || Math.abs(dy) > 4)) moved = true;
            if (moved) {
                e.preventDefault();
                void store.session?.scroll(dy);
            }
            lastTouch = { x: e.touches[0].clientX, y: e.touches[0].clientY };
        };
        const onTouchEnd = (e: TouchEvent) => {
            const t = e.changedTouches[0];
            if (!moved && t) {
                const pt = getVideoPoint(t.clientX, t.clientY);
                if (pt) {
                    void store.session?.mouse_move(pt.x, pt.y);
                    void store.session?.mouse_button(0, true);
                    void store.session?.mouse_button(0, false);
                }
            }
            lastTouch = null;
            moved = false;
        };

        // ── mouse (desktop browser using mobile emulation) ────────────────────
        const MOVE_INTERVAL = 100;
        let lastMoveAt = 0;
        let pendingMoveTimer: ReturnType<typeof setTimeout> | null = null;
        const sendMove = (pt: { x: number; y: number }) => {
            lastMoveAt = Date.now();
            void store.session?.mouse_move(pt.x, pt.y);
        };
        const onMouseMove = (e: MouseEvent) => {
            const pt = getVideoPoint(e.clientX, e.clientY);
            if (!pt) return;
            const elapsed = Date.now() - lastMoveAt;
            if (elapsed >= MOVE_INTERVAL) {
                if (pendingMoveTimer !== null) { clearTimeout(pendingMoveTimer); pendingMoveTimer = null; }
                sendMove(pt);
            } else {
                const captured = pt;
                if (pendingMoveTimer !== null) clearTimeout(pendingMoveTimer);
                pendingMoveTimer = setTimeout(() => { pendingMoveTimer = null; sendMove(captured); }, MOVE_INTERVAL - elapsed);
            }
        };
        const onMouseDown = (e: MouseEvent) => {
            el.focus();
            void store.session?.mouse_button(e.button, true);
        };
        const onMouseUp = (e: MouseEvent) => {
            void store.session?.mouse_button(e.button, false);
        };
        const onWheel = (e: WheelEvent) => {
            e.preventDefault();
            void store.session?.scroll(e.deltaY);
        };
        const onKeyDown = (e: KeyboardEvent) => {
            e.preventDefault();
            const keysym = keyToKeysym(e);
            if (keysym) void store.session?.key(keysym, true);
        };
        const onKeyUp = (e: KeyboardEvent) => {
            e.preventDefault();
            const keysym = keyToKeysym(e);
            if (keysym) void store.session?.key(keysym, false);
        };

        el.addEventListener("touchstart", onTouchStart, { passive: true });
        el.addEventListener("touchmove", onTouchMove, { passive: false });
        el.addEventListener("touchend", onTouchEnd, { passive: false });
        el.addEventListener("mousemove", onMouseMove);
        el.addEventListener("mousedown", onMouseDown);
        el.addEventListener("mouseup", onMouseUp);
        el.addEventListener("wheel", onWheel, { passive: false });
        el.addEventListener("keydown", onKeyDown);
        el.addEventListener("keyup", onKeyUp);
        return () => {
            if (pendingMoveTimer !== null) { clearTimeout(pendingMoveTimer); pendingMoveTimer = null; }
            el.removeEventListener("touchstart", onTouchStart);
            el.removeEventListener("touchmove", onTouchMove);
            el.removeEventListener("touchend", onTouchEnd);
            el.removeEventListener("mousemove", onMouseMove);
            el.removeEventListener("mousedown", onMouseDown);
            el.removeEventListener("mouseup", onMouseUp);
            el.removeEventListener("wheel", onWheel);
            el.removeEventListener("keydown", onKeyDown);
            el.removeEventListener("keyup", onKeyUp);
        };
    }, [store.isOpen, store.proxyViewMode, store.browserSessionId]);

    // Mouse and keyboard input
    useEffect(() => {
        const el = overlayRef.current;
        if (!el || !store.isOpen || store.proxyViewMode !== "desktop") return;
        const sid = store.browserSessionId;

        const getVideoPoint = (clientX: number, clientY: number) => {
            const video = videoRef.current;
            if (!video) return null;
            const rect = video.getBoundingClientRect();
            const zoom = parseFloat(video.style.zoom) || 1;
            return {
                x: Math.round((clientX - rect.left) / zoom),
                y: Math.round((clientY - rect.top) / zoom),
            };
        };

        const MOVE_INTERVAL = 100;
        let lastMoveAt = 0;
        let pendingMoveTimer: ReturnType<typeof setTimeout> | null = null;
        const sendMove = (pt: { x: number; y: number }) => {
            lastMoveAt = Date.now();
            void store.session?.mouse_move(pt.x, pt.y);
        };
        const onMouseMove = (e: MouseEvent) => {
            const pt = getVideoPoint(e.clientX, e.clientY);
            if (!pt) return;
            const elapsed = Date.now() - lastMoveAt;
            if (elapsed >= MOVE_INTERVAL) {
                if (pendingMoveTimer !== null) { clearTimeout(pendingMoveTimer); pendingMoveTimer = null; }
                sendMove(pt);
            } else {
                const captured = pt;
                if (pendingMoveTimer !== null) clearTimeout(pendingMoveTimer);
                pendingMoveTimer = setTimeout(() => { pendingMoveTimer = null; sendMove(captured); }, MOVE_INTERVAL - elapsed);
            }
        };
        const onMouseDown = (e: MouseEvent) => {
            el.focus();
            void store.session?.mouse_button(e.button, true);
        };
        const onMouseUp = (e: MouseEvent) => {
            void store.session?.mouse_button(e.button, false);
        };
        const onWheel = (e: WheelEvent) => {
            e.preventDefault();
            void store.session?.scroll(e.deltaY);
        };
        const onKeyDown = (e: KeyboardEvent) => {
            e.preventDefault();
            const keysym = keyToKeysym(e);
            if (keysym) void store.session?.key(keysym, true);
        };
        const onKeyUp = (e: KeyboardEvent) => {
            e.preventDefault();
            const keysym = keyToKeysym(e);
            if (keysym) void store.session?.key(keysym, false);
        };
        const onMouseLeave = () => { el.style.cursor = ""; };

        el.addEventListener("mousemove", onMouseMove);
        el.addEventListener("mousedown", onMouseDown);
        el.addEventListener("mouseup", onMouseUp);
        el.addEventListener("wheel", onWheel, { passive: false });
        el.addEventListener("keydown", onKeyDown);
        el.addEventListener("keyup", onKeyUp);
        el.addEventListener("mouseleave", onMouseLeave);

        return () => {
            if (pendingMoveTimer !== null) { clearTimeout(pendingMoveTimer); pendingMoveTimer = null; }
            el.removeEventListener("mousemove", onMouseMove);
            el.removeEventListener("mousedown", onMouseDown);
            el.removeEventListener("mouseup", onMouseUp);
            el.removeEventListener("wheel", onWheel);
            el.removeEventListener("keydown", onKeyDown);
            el.removeEventListener("keyup", onKeyUp);
            el.removeEventListener("mouseleave", onMouseLeave);
        };
    }, [store.isOpen, store.proxyViewMode, store.browserSessionId]);

    const sendKey = (keysym: number, pressed: boolean) => { void store.session?.key(keysym, pressed); };

    // DevTools resize drag
    const devtoolsResizeRef = useRef<{ startY: number; startH: number } | null>(null);
    const [devtoolsResizeDragging, setDevtoolsResizeDragging] = useState(false);

    const onDevtoolsResizeStart = (e: React.MouseEvent) => {
        e.preventDefault();
        devtoolsResizeRef.current = { startY: e.clientY, startH: store.devtoolsHeight };
        setDevtoolsResizeDragging(true);
        const onMove = (ev: MouseEvent) => {
            if (!devtoolsResizeRef.current) return;
            const dy = devtoolsResizeRef.current.startY - ev.clientY;
            store.setDevtoolsHeight(devtoolsResizeRef.current.startH + dy);
        };
        const onUp = () => {
            devtoolsResizeRef.current = null;
            setDevtoolsResizeDragging(false);
            window.removeEventListener("mousemove", onMove);
            window.removeEventListener("mouseup", onUp);
        };
        window.addEventListener("mousemove", onMove);
        window.addEventListener("mouseup", onUp);
    };

    // Console expression input
    const [consoleInput, setConsoleInput] = useState("");
    const consoleInputRef = useRef<HTMLInputElement>(null);

    const executeConsole = () => {
        const expr = consoleInput.trim();
        if (!expr) return;
        setConsoleInput("");
        store.executeJs(expr);
    };

    const devtoolsPanel = store.devtoolsOpen ? (
        <DevtoolsPanel store={store} onResizeStart={onDevtoolsResizeStart} resizeDragging={devtoolsResizeDragging} consoleInput={consoleInput} setConsoleInput={setConsoleInput} onConsoleSubmit={executeConsole} consoleInputRef={consoleInputRef} />
    ) : null;

    const selectPreset = (width: number, height: number, mode: "desktop" | "mobile") => {
        store.setProxyViewMode(mode);
        userZoomRef.current = 1;
        setUserZoom(1);
        setDisplayConfigOpen(false);
        setVideoLoading(true);
        void store.session?.resize(width, height);
    };

    const selectQuality = (bitrate: number, framerate: number) => {
        setDisplayConfigOpen(false);
        setVideoLoading(true);
        void store.session?.set_quality(bitrate, framerate);
    };

    const isInContext = appStore.state.isContextRef(store.browserSessionId);
    const toggleBrowserContext = () => {
        appStore.state.toggleContextRef({
            type: "browser",
            id: store.browserSessionId,
            label: store.url || store.browserSessionId,
        });
    };

    const contentControls = (
        <div ref={controlsRef} className="proxy_modal__content_controls">
            {displayConfigOpen && (
                <div className="proxy_modal__display_config">
                    <div className="proxy_modal__display_config_section">Desktop</div>
                    {DESKTOP_PRESETS.map(p => (
                        <button
                            key={p.label}
                            className={"proxy_modal__display_config_item" + (store.session?.width === p.width && store.session?.height === p.height ? " active" : "")}
                            onClick={() => selectPreset(p.width, p.height, "desktop")}
                        >
                            <span className="proxy_modal__display_config_label">{p.label}</span>
                            <span className="proxy_modal__display_config_desc">{p.desc}</span>
                        </button>
                    ))}
                    <div className="proxy_modal__display_config_section">Mobile</div>
                    {MOBILE_PRESETS.map(p => (
                        <button
                            key={p.label}
                            className={"proxy_modal__display_config_item" + (store.session?.width === p.width && store.session?.height === p.height ? " active" : "")}
                            onClick={() => selectPreset(p.width, p.height, "mobile")}
                        >
                            <span className="proxy_modal__display_config_label">{p.label}</span>
                            <span className="proxy_modal__display_config_desc">{p.desc}</span>
                        </button>
                    ))}
                    <div className="proxy_modal__display_config_section">Quality</div>
                    {QUALITY_PRESETS.map(p => (
                        <button
                            key={p.label}
                            className={"proxy_modal__display_config_item" + (store.session?.bitrate === p.bitrate && store.session?.framerate === p.framerate ? " active" : "")}
                            onClick={() => selectQuality(p.bitrate, p.framerate)}
                        >
                            <span className="proxy_modal__display_config_label">{p.label}</span>
                            <span className="proxy_modal__display_config_desc">{p.desc}</span>
                        </button>
                    ))}
                </div>
            )}
            <button
                className={"proxy_modal__btn" + (isInContext ? " active" : "")}
                onClick={toggleBrowserContext}
                title={isInContext ? "Remove from agent context" : "Add to agent context"}
            >@</button>
            {store.proxyViewMode === "desktop" && (
                <button
                    className={"proxy_modal__btn" + (pinchEnabled ? " active" : "")}
                    onClick={() => setPinchEnabled(v => { pinchEnabledRef.current = !v; return !v; })}
                    title={pinchEnabled ? "Disable pinch zoom" : "Enable pinch zoom"}
                >🤏</button>
            )}
            <button
                className={"proxy_modal__btn" + (displayConfigOpen ? " active" : "")}
                onClick={() => setDisplayConfigOpen(v => !v)}
                title="Display settings"
            >⊞</button>
        </div>
    );

    const browserArea = (
        <div className="proxy_modal__browser_area">
            <div
                ref={scrollWrapperRef}
                className={"proxy_modal__scroller" + (store.proxyViewMode === "mobile" ? " proxy_modal__scroller--mobile" : "")}
            >
                <div ref={zoomWrapperRef}>
                    <video
                        ref={videoRef}
                        className="proxy_modal__frame"
                        autoPlay
                        playsInline
                        muted
                    />
                </div>
            </div>
            {videoLoading && (
                <div className="proxy_modal__preloader">
                    <div className="proxy_modal__spinner" />
                </div>
            )}
            <div
                ref={overlayRef}
                className="proxy_modal__pinch_overlay"
                tabIndex={0}
                style={{
                    outline: "none",
                    cursor: store.proxyViewMode === "desktop" ? store.cssCurrentCursor : "default",
                }}
            />
            {contentControls}
        </div>
    );

    return (
        <div
            className={"proxy_modal " + SWIPE_GUARD_CLASS + (store.isOpen ? " proxy_modal--open" : "") + (store.proxyViewMode === "mobile" ? " proxy_modal--mobile-view" : "")}
        >
            <div className="proxy_modal__toolbar">
                <button
                    className="proxy_modal__btn"
                    title="Back"
                    onClick={() => {
                        sendKey(0xFFE9, true);
                        sendKey(0xFF51, true);
                        sendKey(0xFF51, false);
                        sendKey(0xFFE9, false);
                    }}
                >←</button>
                <button
                    className="proxy_modal__btn"
                    title="Reload"
                    onClick={() => {
                        sendKey(0xFFC2, true);
                        sendKey(0xFFC2, false);
                    }}
                >↻</button>
                <div className="proxy_modal__url_form">
                    <TextInput
                        className="proxy_modal__url_input"
                        value={urlInput}
                        onChange={v => setUrlInput(v)}
                        submitOnEnter
                        selectOnFocus
                        onFocus={() => { urlInputFocusedRef.current = true; }}
                        onBlur={() => { urlInputFocusedRef.current = false; }}
                        onEnter={v => {
                            const url = v.trim();
                            if (url) store.navigate(url);
                        }}
                    />
                </div>
                <button
                    className={"proxy_modal__btn" + (store.devtoolsOpen ? " active" : "")}
                    title="DevTools"
                    onClick={() => store.toggleDevtools()}
                >{"≡"}</button>
            </div>
            <div className="proxy_modal__content">
                {browserArea}
                {devtoolsPanel}
            </div>
        </div>
    );
}, BrowserStreamModalStore);

const DevtoolsPanel = observer((props: {
    store: BrowserStreamModalStore;
    onResizeStart: (e: React.MouseEvent) => void;
    resizeDragging: boolean;
    consoleInput: string;
    setConsoleInput: (v: string) => void;
    onConsoleSubmit: () => void;
    consoleInputRef: React.RefObject<HTMLInputElement>;
}) => {
    const { store, onResizeStart, resizeDragging, consoleInput, setConsoleInput, onConsoleSubmit, consoleInputRef } = props;
    const consoleEntries = store.consoleEntries;
    const allNetworkRequests = store.networkRequests;
    const networkRequests = allNetworkRequests.filter(r => matchesNetworkFilter(r.resourceType, store.networkFilter));
    const errorCount = store.errorCount;
    const consoleBodyRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (store.devtoolsTab === "console" && consoleBodyRef.current) {
            consoleBodyRef.current.scrollTop = consoleBodyRef.current.scrollHeight;
        }
    }, [consoleEntries.length, store.devtoolsTab]);

    const netBodyRef = useRef<HTMLDivElement>(null);
    useEffect(() => {
        if (store.devtoolsTab === "network" && netBodyRef.current) {
            netBodyRef.current.scrollTop = netBodyRef.current.scrollHeight;
        }
    }, [networkRequests.length, store.devtoolsTab]);

    return (
        <div
            className="proxy_modal__devtools"
            style={{ height: store.devtoolsHeight }}
        >
            <div
                className={"proxy_modal__devtools_resize" + (resizeDragging ? " proxy_modal__devtools_resize--dragging" : "")}
                onMouseDown={onResizeStart}
            />
            {store.devtoolsOpening && (
                <div className="proxy_modal__preloader proxy_modal__preloader--overlay">
                    <div className="proxy_modal__spinner" />
                </div>
            )}
            <div className="proxy_modal__devtools_tabs">
                <button
                    className={"proxy_modal__devtools_tab" + (store.devtoolsTab === "network" ? " active" : "")}
                    onClick={() => store.setDevtoolsTab("network")}
                >
                    Network
                    {allNetworkRequests.length > 0 && (
                        <span className="proxy_modal__devtools_badge">{allNetworkRequests.length}</span>
                    )}
                </button>
                <button
                    className={"proxy_modal__devtools_tab" + (store.devtoolsTab === "console" ? " active" : "")}
                    onClick={() => store.setDevtoolsTab("console")}
                >
                    Console
                    {errorCount > 0 && (
                        <span className="proxy_modal__devtools_badge proxy_modal__devtools_badge--error">{errorCount}</span>
                    )}
                </button>
                <button
                    className="proxy_modal__devtools_clear"
                    onClick={() => {
                        if (store.devtoolsTab === "network") void store.session?.clear_network_log();
                        else void store.session?.clear_console();
                    }}
                >Clear</button>
            </div>

            {store.devtoolsTab === "network" && (
                <>
                    <div className="proxy_modal__devtools_filter">
                        {NETWORK_FILTERS.map(f => (
                            <button
                                key={f}
                                className={"proxy_modal__devtools_filter_btn" + (store.networkFilter === f ? " active" : "")}
                                onClick={() => store.setNetworkFilter(f as NetworkFilter)}
                            >{f}</button>
                        ))}
                    </div>
                    <div ref={netBodyRef} className="proxy_modal__devtools_body">
                        {networkRequests.length === 0
                            ? <div className="proxy_modal__devtools_empty">No requests</div>
                            : networkRequests.map(r => {
                                const statusCls = r.failed ? "--err" : r.status == null ? "--pending" : r.status < 400 ? "--ok" : "--err";
                                const durMs = r.duration != null ? (r.duration * 1000) : null;
                                const durLabel = durMs == null ? "…" : durMs < 1000 ? `${Math.round(durMs)}ms` : `${(durMs / 1000).toFixed(1)}s`;
                                const shortUrl = (() => { try { const u = new URL(r.url); return u.pathname + u.search; } catch { return r.url; } })();
                                return (
                                    <div key={r.requestId} className="proxy_modal__devtools_net_entry" title={r.url}>
                                        <span className="proxy_modal__devtools_net_method">{r.method}</span>
                                        <span className="proxy_modal__devtools_net_type">{r.resourceType}</span>
                                        <span className="proxy_modal__devtools_net_url">{shortUrl}</span>
                                        <span className={"proxy_modal__devtools_net_status proxy_modal__devtools_net_status" + statusCls}>
                                            {r.failed ? "ERR" : r.status ?? "…"}
                                        </span>
                                        <span className="proxy_modal__devtools_net_dur">{durLabel}</span>
                                    </div>
                                );
                            })
                        }
                    </div>
                </>
            )}

            {store.devtoolsTab === "console" && (
                <>
                    <div ref={consoleBodyRef} className="proxy_modal__devtools_body">
                        {consoleEntries.length === 0
                            ? <div className="proxy_modal__devtools_empty">No console output</div>
                            : consoleEntries.map((e, i) => {
                                const level = e.level === "exception" ? "error" : e.level;
                                const levelMod = level === "error" ? "--error" : level === "warn" ? "--warn" : level === "debug" ? "--debug" : "";
                                const levelIcon = level === "error" ? "✕" : level === "warn" ? "⚠" : level === "info" ? "i" : "›";
                                return (
                                    <div key={i} className={"proxy_modal__devtools_entry" + (levelMod ? ` proxy_modal__devtools_entry${levelMod}` : "")}>
                                        <span className="proxy_modal__devtools_entry_level">{levelIcon}</span>
                                        <span className="proxy_modal__devtools_entry_text">{e.text}</span>
                                    </div>
                                );
                            })
                        }
                    </div>
                    <div className="proxy_modal__console_input_area">
                        <span style={{ color: "var(--color-text-muted)", fontFamily: "monospace", fontSize: 13, userSelect: "none" }}>{">"}</span>
                        <input
                            ref={consoleInputRef}
                            className="proxy_modal__console_input"
                            placeholder="Execute JavaScript…"
                            value={consoleInput}
                            onChange={e => setConsoleInput(e.target.value)}
                            onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); onConsoleSubmit(); } }}
                            spellCheck={false}
                            autoComplete="off"
                        />
                    </div>
                </>
            )}
        </div>
    );
});
